import { useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, Building2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = {
  name: "", work_email: "", company: "", phone: "",
  device_type: "", market_focus: "", use_case: "",
};

/**
 * Enterprise "Contact sales" dialog — anonymous, rate-limited server-side.
 * Opens from the Pricing page Enterprise CTA.
 */
export default function ContactSalesDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const isValid =
    form.name.trim().length >= 2 &&
    /.+@.+\..+/.test(form.work_email) &&
    form.company.trim().length >= 2 &&
    form.use_case.trim().length >= 10;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/contact`, {
        name: form.name.trim(),
        work_email: form.work_email.trim(),
        company: form.company.trim(),
        phone: form.phone.trim() || null,
        device_type: form.device_type.trim() || null,
        market_focus: form.market_focus.trim() || null,
        use_case: form.use_case.trim(),
      });
      setDone(true);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) setError("You've submitted a few requests already. Please try again in a few minutes.");
      else if (status === 400 && typeof detail === "string") setError(detail);
      else if (status === 422) setError("Please double-check the required fields.");
      else setError("We couldn't send that right now. Please try again or email hello@vitaqms.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = (v) => {
    if (submitting) return;
    onOpenChange(v);
    if (!v) {
      setTimeout(() => { setForm(EMPTY); setDone(false); setError(null); }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-sales-dialog">
        <DialogHeader>
          <div className="vq-overline mb-1 flex items-center gap-2">
            <Building2 className="w-3 h-3" /> Enterprise
          </div>
          <DialogTitle className="font-display font-bold text-2xl">Talk to sales</DialogTitle>
          <DialogDescription>
            Enterprise pricing covers multi-workspace, custom SSO, dedicated success, and on-prem data-residency options. Tell us a bit and we&rsquo;ll get back within one business day.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 flex items-start gap-3" data-testid="contact-sales-success">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-emerald-900">Thanks — we&rsquo;ve got it.</div>
              <div className="text-sm text-emerald-800 mt-1">A member of the VitaQMS team will reach out to <b>{form.work_email}</b> within one business day.</div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Name *</Label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Work email *</Label>
                <Input data-testid="contact-email" type="email" value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} maxLength={160} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Company *</Label>
                <Input data-testid="contact-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} maxLength={160} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Phone (optional)</Label>
                <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} maxLength={40} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device type</Label>
                <Input data-testid="contact-device" placeholder="e.g. Infusion pump, SaMD" value={form.device_type} onChange={(e) => setForm({ ...form, device_type: e.target.value })} maxLength={200} />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Target markets</Label>
                <Input data-testid="contact-markets" placeholder="e.g. US + EU" value={form.market_focus} onChange={(e) => setForm({ ...form, market_focus: e.target.value })} maxLength={200} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Use case *</Label>
              <Textarea
                data-testid="contact-use-case"
                rows={4} maxLength={1200}
                placeholder="What are you building, where are you in the regulatory journey, and how many devices/team members would use VitaQMS?"
                value={form.use_case}
                onChange={(e) => setForm({ ...form, use_case: e.target.value })}
                className="resize-none"
              />
              <div className="text-[10px] text-slate-400 mt-0.5 text-right font-mono">{form.use_case.length}/1200</div>
            </div>

            {error && (
              <div className="p-2.5 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="contact-error">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900">{error}</div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={() => close(false)} className="bg-slate-900 hover:bg-[#0055FF] text-white" data-testid="contact-close">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => close(false)} disabled={submitting}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={!isValid || submitting}
                data-testid="contact-submit"
                className="bg-slate-900 hover:bg-[#0055FF] text-white"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send to sales"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
